import React, { useContext, useState, useEffect, useRef } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  TextField,
} from "@mui/material";
import { AppContext } from "../../AppContextProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";

import {
  createQuote,
} from "../../api/ProcurementClient";
import { AxiosError } from "axios";
import User, { Role } from "../../models/User";
import Customer from "../../models/Customer";
import Rfq from "../../models/Rfq";
import dayjs, { Dayjs } from "dayjs";
import { DateTimeUtils } from "../../DateTimeUtils";
import CustomerAutocomplete from "../CustomerAutocomplete";
import BuyerAutocomplete, {
  BuyerAutocompleteRef,
} from "../BuyerAutocomplete";
import CustomerContact, { CustomerContactRole } from "../../models/CustomerContact";
import RfqPrioritySelector from "./RfqPrioritySelector";
import { ensureCustomerHydrated } from "../../utils/sessionCatalogCache";
import { mergeRfqIntoList } from "../../utils/mergeRfqIntoList";

interface DialogProps {
  onClose: (rfq?: Rfq, editMode?: boolean) => void;
}

const CreateRfqDialog: React.FC<DialogProps> = ({ onClose }) => {
  const {
    setRfqs,
    users,
    customers,
    setCustomers,
    setSnackbarErrorMsg,
    setSnackbarMsg,
  } = useContext(AppContext);

  const [referenceNum, setReferenceNum] = useState("");
  const [priority, setPriority] = useState(1);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | undefined>();
  const [selectedBuyer, setSelectedBuyer] = useState<CustomerContact | undefined>();
  const [assignedUser, setAssignedUser] = useState<User>();
  const referenceInputRef = useRef<HTMLInputElement>(null);
  const assignToInputRef = useRef<HTMLInputElement>(null);
  const buyerAutocompleteRef = useRef<BuyerAutocompleteRef>(null);
  const dueDateInputRef = useRef<HTMLInputElement>(null);
  const [dueDate, setDueDate] = useState<Dayjs | null>(dayjs().add(1, "week"));

  useEffect(() => {
    // Focus on the reference input when dialog opens
    if (referenceInputRef.current) {
      referenceInputRef.current.focus();
    }
  }, []);

  const isSaveDisabled = !referenceNum.length || !selectedCustomer || !selectedBuyer || !dueDate;

  const handleDueDateChange = (newValue: Dayjs | null, confirmed: boolean) => {
    setDueDate(newValue);
    if (confirmed) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          assignToInputRef.current?.focus();
        });
      });
    }
  };

  const handleOk = async (editMode?: boolean) => {
    try {
      const newQuote = await createQuote({
        customerId: selectedCustomer!.id,
        buyerId: selectedBuyer!.id,
        referenceNum: referenceNum,
        assignedToId: assignedUser?.id,
        dueDate: DateTimeUtils.toString(dueDate!.toDate()),
        priority,
      });

      setRfqs((prev) => mergeRfqIntoList(prev, newQuote));

      setSnackbarMsg("Quote created successfully");
      onClose(newQuote, editMode);
    } catch (error) {
      const axiosError = error as AxiosError;
      setSnackbarErrorMsg(
        "Error creating quote: " +
        (axiosError.response?.data?.toString() ??
          axiosError.response?.statusText ??
          "Unknown server error")
      );
    }
  };



  const handleCustomerChanged = async (
    customer: Customer | undefined,
    isNew: boolean,
    confirmed: boolean
  ) => {
    setSelectedCustomer(customer);
    setSelectedBuyer(undefined);

    if (customer?.id) {
      // Directory-lite session catalogs omit contacts — hydrate before picking a buyer.
      const hydrated =
        (await ensureCustomerHydrated(customer.id, customers, setCustomers)) ??
        customer;
      setSelectedCustomer(hydrated);
      const buyer = (hydrated.contacts ?? []).find(
        (c) => c.role === CustomerContactRole.BUYER
      );
      setSelectedBuyer(buyer);
    }

    if (confirmed) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          buyerAutocompleteRef.current?.focus();
        });
      });
    }
  };


  const handleBuyerChanged = async (
    buyer: CustomerContact | undefined,
    isNew: boolean,
    confirmed: boolean
  ) => {

    setSelectedBuyer(buyer);
    if (confirmed) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          dueDateInputRef.current?.focus();
        });
      });
    }
  };

  return (
    <>
      <Dialog
        open={true}
        onClose={() => { }}
        fullWidth={true}
        maxWidth="sm"
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        disableRestoreFocus={true}
      >
        <DialogTitle>Create RFQ</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              pt: 1,
              gap: 2,
            }}
          >
            <TextField
              fullWidth
              required={true}
              label="RFQ #"
              variant="standard"
              value={referenceNum}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setReferenceNum(event.target.value)
              }
              inputRef={referenceInputRef}
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <RfqPrioritySelector
                      priority={priority}
                      onPriorityChange={setPriority}
                    />
                  </InputAdornment>
                ),
              }}
            />
            <CustomerAutocomplete
              customer={selectedCustomer}
              onCustomerSelected={handleCustomerChanged}
            />
            <BuyerAutocomplete
              ref={buyerAutocompleteRef}
              customer={selectedCustomer}
              selectedBuyer={selectedBuyer}
              onBuyerSelected={handleBuyerChanged}
            />

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="RFQ Due Date"
                value={dueDate}
                onChange={(newValue) => handleDueDateChange(newValue, false)}
                onAccept={(newValue) => {
                  handleDueDateChange(newValue, true);
                }}
                slotProps={{
                  textField: {
                    variant: "standard",
                    fullWidth: true,
                    inputRef: dueDateInputRef,
                  },
                  openPickerButton: {
                    color: "primary",
                    sx: { padding: "0px" },
                  },
                }}
              />
            </LocalizationProvider>

            <Autocomplete
              options={users
                .filter(
                  (user) =>
                    user.role === Role.ADMIN || user.role === Role.EMPLOYEE
                )
                .sort((a, b) => {
                  const aName = a.name || `${a.firstName} ${a.lastName}`;
                  const bName = b.name || `${b.firstName} ${b.lastName}`;
                  return aName.toLowerCase().localeCompare(bName.toLowerCase());
                })}
              getOptionLabel={(option) =>
                option.name || `${option.firstName} ${option.lastName}`
              }
              autoHighlight={true}
              value={assignedUser || null}
              onChange={(event, newValue) => {
                setAssignedUser(newValue || undefined);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Assign To"
                  variant="standard"
                  inputRef={assignToInputRef}
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => onClose()}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => handleOk(false)}
            disabled={isSaveDisabled}
          >
            Save & Close
          </Button>
          <Button
            variant="contained"
            onClick={() => handleOk(true)}
            disabled={isSaveDisabled}
          >
            Save & Add Items
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateRfqDialog;
